import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';
import {
  BluetoothState,
  CameraType,
  ConnectionStatus,
  LBSPeripherals,
  ScannerType,
  TerminalType,
  TrannsactionResult,
} from 'plugins/lbs-peripherals/dist/esm';
import { PeripheralsService, PreviewFrame } from '../peripherals.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { LoadingService } from 'src/app/_services/loading/loading.service';
import { ErrorService } from 'src/app/_services/error/error.service';
import { LBSErrorHandlerService } from 'src/app/_services/error-handler/error-handler.service';
import { IMSId } from 'src/app/_model/ims-object';
import { Peripheral, PeripheralType } from 'src/app/_model/peripheral';
import { PeripheralsDaoService } from '../../dao/peripherals-dao.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { PeripheralError } from '../peripheral-errors';
import { ModalController } from '@ionic/angular';
import { PeripheralConnectionEstablishModalComponent } from 'src/app/_components/peripheral-connection-establish-modal/peripheral-connection-establish-modal.component';
import {
  BluetoothDevice,
  CodeId,
  ConnectResult,
  ScannerCommand,
} from 'plugins/lbs-peripherals/src';
import {
  EventService,
  IMSEvent,
  IMSEventContentTypeHook,
  IMSEventContentTypeScanner,
  IMSEventMessageType,
  IMSEventSourceType,
  IMSInternalEventHook,
} from 'src/app/_services/event/event.service';
import { IMSEventListener } from 'src/app/_services/event/event-listener';
import { PreferencesService } from 'src/app/_services/preferences/preferences.service';
import { APILevel } from 'src/app/_services/sync/api-level';
import { Capacitor } from '@capacitor/core';
import { ImsErrorHandler } from 'src/app/_util/ims-error-handler';
import { App, AppState } from '@capacitor/app';
import { Servicejob } from 'src/app/_model/servicejob';
import { PaymentReceiptRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/payment-receipt-remote-action-builder copy/payment-receipt-remoteaction-builder';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { TourGPSStatus } from 'src/app/_model/tour';
import { TourService } from 'src/app/_services/tour/logic/tour.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { v4 as uuidv4 } from 'uuid';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { ImsImage } from 'src/app/_model/ims-image';
import { parse } from 'date-fns';
import { FileManagerService } from 'src/app/_services/file-manager/file-manager.service';
import { Filesystem } from '@capacitor/filesystem';
import { PeripheralCameraModalComponent } from 'src/app/_components/peripheral-camera-modal/peripheral-camera-modal.component';
import { PeripheralGallerySelectModalPage } from 'src/app/_pages/peripheral-gallery-select-modal/peripheral-gallery-select-modal.page';
import { ExtFile } from 'src/app/_model/ext-file';
import { CapacitorHttp } from '@capacitor/core';
import { LBSUtils } from 'src/app/_util/lbs-utils';

type DeviceStatus = {
  connection?: ConnectionStatus;
  battery?: number;
};
@Injectable()
export class PeripheralsLogicService implements PeripheralsService, IMSEventListener {
  // Public Member
  public bluetooth$ = new BehaviorSubject<BluetoothState>(
    BluetoothState.UNKNOWN
  );
  public peripherals$ = new BehaviorSubject<Peripheral[]>([]);
  public poolPeripherals$ = new BehaviorSubject<Peripheral[]>([]);
  public allPeripherals$ = combineLatest([this.peripherals$, this.poolPeripherals$]).pipe(
    map(([peripherals, poolPeripherals]) => [...peripherals, ...poolPeripherals]),
  );
  public get allPeripherals(): Peripheral[] {
    return [...this.peripherals$.getValue(), ...this.poolPeripherals$.getValue()];
  }
  public bluetoothDevices$ = new BehaviorSubject<BluetoothDevice[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);
  public previewFrames$ = new Subject<PreviewFrame>();

  private peripheralStatusMap = new Map<string, DeviceStatus>();
  private assignedPoolPeripheralIds = new Set<number>();

  private tempPeripherals: Peripheral[];

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private peripheralsDaoService: PeripheralsDaoService,
    private authenticationService: AuthenticationService,
    private remoteActionService: RemoteActionService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    private errorHandlerService: LBSErrorHandlerService,
    private errorHandler: ImsErrorHandler,
    private modalController: ModalController,
    private zone: NgZone,
    private eventService: EventService,
    private appService: AppService,
    private credentialsService: CredentialsService,
    private tourService: TourService,
    private servicejobsService: ServicejobsService,
    private appParameterService: AppParameterService,
    private fileManager: FileManagerService,
    private preferencesService: PreferencesService,
  ) {
    this.initialize();
    this.eventService.registerListener(this, 1000);
    LBSPeripherals.addListener('stateChanged', (info: any) => {
      console.log('LBSPeripherals received state:', info, info?.bluetooth);
      this.bluetooth$.next(info?.bluetooth ?? BluetoothState.UNKNOWN);
    });
    LBSPeripherals.addListener(
      'scan',
      (scan: { code: CodeId; message: string; device: string }) => {
        console.log('LBSPeripherals received scan:', scan);
        if (scan != null) {
          this.zone.run(() => {
            let contentType: IMSEventContentTypeScanner = {
              codeId: scan.code,
              kind: IMSEventMessageType.CODE,
            };
            let event: IMSEvent = {
              message: scan.message,
              source: {
                objId: this.getPeripheralForMac(scan.device).id,
                type: IMSEventSourceType.SCANNER,
                contentType: contentType,
              },
            };
            this.eventService.sendEvent(
              event,
              this.appParameterService.appParameters$.getValue()
            );
          });
        }
      }
    );
    LBSPeripherals.addListener('deviceStatusChanged', (state: any) => {
      console.log(
        'LBSPeripherals received deviceStatusChanged:',
        state?.device,
        state?.status,
        state?.battery
      );
      if (state) {
        this.zone.run(() => {
          let status: DeviceStatus =
            this.peripheralStatusMap.get(state.device) ?? {};
          console.log(
            'LBSPeripherals found peripheral status in map:',
            status.connection,
            status.battery
          );
          if (state.status != undefined && status != state.status) {
            status.connection = state.status;
          }
          if (state.battery != undefined) {
            status.battery = state.battery;
          }
          console.log(
            'LBSPeripherals new peripheral status:',
            status.connection,
            status.battery
          );
          this.peripheralStatusMap.set(state.device, status);
          console.log(
            'LBSPeripherals status map:',
            this.peripheralStatusMap,
            state.device
          );
          this.updatePeripheralsWithStatus(this.peripherals$.getValue());
        });
      }
    });
    LBSPeripherals.addListener('previewFrame', (frame: PreviewFrame) => {
      this.previewFrames$.next(frame);
    });
    App.addListener('appStateChange', async (state: AppState) => {
      if (state.isActive) {
        await this.connectAvailableDevices();
      }
    });
  }

  // Resetable
  public async reset(): Promise<void> {
    for (const peripheral of this.peripherals$.getValue()) {
      try {
        await this.tryDisconnectPeripheral(peripheral);
      } catch (e) {
        this.logger.warn(
          this.constructor.name,
          'could not disconnect peripheral on reset',
          peripheral.id?.imsId
        );
      }
    }
    await this.peripheralsDaoService.reset();
    await this.loadLocalPeripherals();
  }
  public async clear(): Promise<void> {
    for (const peripheral of this.peripherals$.getValue()) {
      try {
        await this.tryDisconnectPeripheral(peripheral);
      } catch (e) {
        this.logger.warn(
          this.constructor.name,
          'could not disconnect peripheral on reset',
          peripheral.id?.imsId
        );
      }
    }
    await this.peripheralsDaoService.clear();
    this.peripheralStatusMap.clear();
    this.peripherals$.next([]);
    this.poolPeripherals$.next([]);
  }

  // Syncable
  public async presync(): Promise<void> {
    this.tempPeripherals = [
      ...this.peripherals$.getValue(),
      ...this.poolPeripherals$.getValue(),
    ];
  }
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemotePeripherals(apiLevel, syncKey);
  }
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      const assignedIds = await this.preferencesService.getPoolAssignedPeripherals();
      this.assignedPoolPeripheralIds = new Set(assignedIds);
      await this.loadLocalPeripherals();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // IMSEventListener
  public async processEvent(event: IMSEvent): Promise<boolean> {
    const contentType = event.source?.contentType as IMSEventContentTypeHook;
    if (
      event.source?.type === IMSEventSourceType.INTERNAL &&
      contentType?.kind === IMSEventMessageType.HOOK &&
      contentType?.hook === IMSInternalEventHook.SOD
    ) {
      this.logger.log(this.constructor.name, 'SOD received — clearing pool peripheral assignments');
      this.assignedPoolPeripheralIds.clear();
      await this.preferencesService.clearPoolAssignedPeripherals();
      await this.loadLocalPeripherals();
    }
    return false;
  }

  public async notifyProcessedEventByOtherListener(): Promise<void> {}

  // Pool Peripheral Assignment
  public async assignPoolPeripheral(peripheral: Peripheral): Promise<void> {
    this.assignedPoolPeripheralIds.add(peripheral.id.imsId);
    await this.preferencesService.addPoolAssignedPeripheral(peripheral.id.imsId);
    await this.loadLocalPeripherals();
  }
  async initializeCompleted(): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();
      if (platform !== 'ios' || this.peripherals$.getValue().length > 0) {
        let bt = await LBSPeripherals.getBluetoothState();
        this.bluetooth$.next(bt.state ?? BluetoothState.UNKNOWN);
      }
      this.connectAvailableDevices();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error connecting to available devices: ',
        error?.message
      );
    }
  }

  // Public Functions

  public getConnnectedPeripheralForType(type: PeripheralType): Peripheral {
    if (type == undefined) {
      return undefined;
    }

    return this.peripherals$.getValue().find((peripheral) => {
      if (
        peripheral.type === type &&
        peripheral.connectionStatus == ConnectionStatus.CONNECTED
      ) {
        return true;
      }
      return false;
    });
  }
  public getAvailablePeripheralsForType(type: PeripheralType): Peripheral[] {
    if (type == undefined) {
      return undefined;
    }

    return this.peripherals$.getValue().filter((peripheral) => {
      if (
        peripheral.type === type &&
        peripheral.connectionStatus != ConnectionStatus.NOT_CONNECTED
      ) {
        return true;
      }
      return false;
    });
  }

  public getAllPeripheralForType(type: PeripheralType): Peripheral[] {
    if (type == undefined) {
      return undefined;
    }

    return this.peripherals$.getValue().filter((peripheral) => {
      if (peripheral.type === type) {
        return true;
      }
      return false;
    });
  }

  public getPeripheralForId(id: IMSId): Peripheral {
    if (id === undefined) {
      return undefined;
    }

    return this.peripherals$.getValue().find((peripheral) => {
      if (peripheral.id.imsId === id.imsId) {
        return true;
      }
      return false;
    });
  }

  public getPeripheralForMac(mac: string): Peripheral {
    if (mac === undefined) {
      return undefined;
    }

    return this.peripherals$.getValue().find((peripheral) => {
      if (peripheral.connectedMac === mac) {
        return true;
      }
      return false;
    });
  }

  public async tryStartBluetooth(): Promise<void> {
    console.log(this.constructor.name, 'LBSPeripherals tryStartBluetooth');
    try {
      await LBSPeripherals.activateBluetooth();
    } catch (e) {
      this.errorHandler.handleError(e);
    }
  }

  public async requestBluetoothPermissions(): Promise<void> {
    console.log(
      this.constructor.name,
      'LBSPeripherals requestBluetoothPermissions'
    );
    await LBSPeripherals.requestBluetoothPermission();
  }

  public async requestWifiPermissions(): Promise<void> {
    console.log(
      this.constructor.name,
      'LBSPeripherals requestWifiPermissions'
    );
    await LBSPeripherals.requestWifiPermission();
  }

  public async fetchPeripheralHardwareInfo(
    peripheral: Peripheral
  ): Promise<void> {
    switch (peripheral.type) {
      case PeripheralType.PT_SCANNER_NETUM:
        this.trySendScannerCommand(peripheral, ScannerCommand.BATTERY);
        return;
      case PeripheralType.PT_PAYMENT_SIX:
        LBSPeripherals.getBatteryState({
          id: peripheral.softwareId,
          type: TerminalType.SIX,
        });
        return;
    }
  }

  public async tryConnectPeripheral(
    peripheral: Peripheral
  ): Promise<ConnectResult> {
    console.log(
      this.constructor.name,
      'JB360 LBSPeripherals try connect to peripheral',
      peripheral.name,
      peripheral.connectionStatus
    );
    if (peripheral.connectedMac == null) {
      console.log(
        this.constructor.name,
        'LBSPeripherals peripheral',
        peripheral.name,
        'was never connected - start configuration and bt search'
      );
      let modalId = 'peripheralModalId' + uuidv4();
      console.log('JB Create Modal');
      const modal = await this.modalController.create({
        component: PeripheralConnectionEstablishModalComponent,
        componentProps: {
          modalId: modalId,
          peripheral: peripheral,
          canDismiss: false,
          presentingElement: await this.modalController.getTop(),
        },
        cssClass: 'fullscreen-modal',
        backdropDismiss: false,
        id: modalId,
      });
      console.log('JB Present Modal');
      await modal.present();
      return undefined;
    } else {
      return await this.connectPeripheral(peripheral);
    }
  }

  public async connectPeripheral(
    peripheral: Peripheral,
    btDevice?: BluetoothDevice,
    inBackground = false
  ): Promise<ConnectResult> {
    let result: ConnectResult = {
      success: false,
      btScanStarted: false,
    };
    console.log(
      this.constructor.name,
      'JB360 LBSPeripherals connect to peripheral',
      peripheral.name,
      peripheral.connectedMac
    );
    try {
      // Start Listening for BT Device immediatly, if no btScan was initiated, remove listener later
      const listener = await LBSPeripherals.addListener(
        'btDeviceFound',
        (info: any) => {
          console.log(
            'LBSPeripherals btDeviceFound:',
            info?.devices,
            JSON.parse(info?.devices)
          );
          this.zone.run(() => {
            this.bluetoothDevices$.next(
              info?.devices ? JSON.parse(info?.devices) : []
            );
          });
        }
      );
      switch (peripheral.type) {
        case PeripheralType.PT_SCANNER_NETUM:
          result = await LBSPeripherals.tryConnectScanner({
            type: ScannerType.NETUM,
            lookupName: peripheral.lookupName,
            mac:
              peripheral.connectedMac != null && peripheral.connectedMac != ''
                ? peripheral.connectedMac
                : peripheral.mac,
            deviceMac: btDevice?.mac,
          });
          break;
        case PeripheralType.PT_PAYMENT_SIX:
          result = await LBSPeripherals.tryConnectTerminal({
            type: TerminalType.SIX,
            id: peripheral.softwareId,
            lookupName: peripheral.lookupName,
          });
          console.log(this.constructor.name, 'JBSIX test:', result);
          break;
        case PeripheralType.PT_CAMERA_THETA:
          if (inBackground) {
            peripheral.connectionStatus = ConnectionStatus.NOT_AVAILABLE;
            await this.peripheralsDaoService.updateConnectionStatus(peripheral);
            result = {
              success: false,
              btScanStarted: false,
            };
          } else {
            result = await LBSUtils.addTimeoutToPromise(LBSPeripherals.tryConnectCamera({
              type: CameraType.RICOH,
              serial: peripheral.softwareId,
              ssid: peripheral.lookupName,
              passphrase: peripheral.softwareId,
              macaddress: peripheral.connectedMac
            }), 120000);
          }
          break;
        default:
          this.logger.warn(
            this.constructor.name,
            'Unknown Device',
            peripheral.type
          );
          this.errorService.showErrorHandlerResult(
            await this.errorHandlerService.handleError(
              new Error(PeripheralError.UNKNOWN_DEVICE_TYPE)
            )
          );
      }

      if (result.btScanStarted != true) {
        await listener.remove();
      }
      if (result.success) {
        console.log(
          this.constructor.name,
          'JBPeripheral connection successful',
          result
        );
        if (peripheral.type === PeripheralType.PT_CAMERA_THETA) {
          peripheral.connectionStatus = ConnectionStatus.CONNECTED;
        }
        if (result.connectedMac) {
          peripheral.connectedMac = result.connectedMac;
          await this.peripheralsDaoService.updateConnectionStatus(peripheral);
        }
        if (result.btScanStarted === true && inBackground) {
          await listener.remove();
          switch (peripheral.type) {
            case PeripheralType.PT_SCANNER_NETUM:
              await LBSPeripherals.stopScannerDiscoveryIfNecessary({
                type: ScannerType.NETUM,
              });
              break;
            case PeripheralType.PT_PAYMENT_SIX:
              break;
            default:
              this.logger.warn(
                this.constructor.name,
                'Unknown Device',
                peripheral.type
              );
          }
          // BREAKS RUS - Needed for MTR??
          // peripheral.connectedMac = '';
          await this.peripheralsDaoService.updateConnectionStatus(peripheral);
        }
      } else {
        // console.log(
        //   this.constructor.name,
        //   'JBPeripheral connection not successful'
        // );
        // peripheral.connectedMac = '';
        // await this.peripheralsDaoService.updateConnectionStatus(peripheral);
      }
    } catch (e) {
      console.log(
        this.constructor.name,
        'LBSPeripherals error while connecting to peripheral',
        e?.message
      );
      this.errorService.showErrorHandlerResult(
        await this.errorHandlerService.handleError(e)
      );
      return {
        success: false,
        btScanStarted: false,
      };
    }
    return result;
  }

  // Cameras Begin

  public async startPeripheralPictureFlow(
    peripheral: Peripheral
  ): Promise<ImsImage> {
    this.loadingService.showLoading();
    try {
      console.log("JB360Cam 1")
      const conRest = await this.tryConnectPeripheral(peripheral);
      if (!conRest.success) {
        throw new Error(PeripheralError.RICOH_CONNECTION_ERROR)
      }
      console.log("JB360Cam 2", conRest)
      const modal = await this.modalController.create({
        component: PeripheralCameraModalComponent,
        componentProps: {
          peripheral: peripheral,
        },
        cssClass: 'transparent-modal',
      });
      console.log("JB360Cam 3")
      this.loadingService.dismissLoading();
      console.log("JB360Cam 4")
      await modal.present();
      console.log("JB360Cam 5")
      const { data } = await modal.onDidDismiss();
      // If modal was not canceled simply pass through the data to the caller
      if (data.cancelled) {
        return;
      }

      await this.tryDisconnectPeripheral(peripheral);

      return data?.image;
    } catch (error) {
      console.log("JB360Cam Err")
      this.logger.error(
        this.constructor.name,
        'could not start peripheral picture flow',
        error?.message
      );
      this.loadingService.dismissLoading();
    }
  }

  public async startPeripheralGalleryFlow(
    peripheral: Peripheral,
    maxImages: number
  ): Promise<ImsImage[]> {
    console.log("RICOH startPeripheralGalleryFlow")
    await this.loadingService.showLoading();
    const conRest = await this.tryConnectPeripheral(peripheral);
    console.log("RICOH connected to peripheral: ", conRest.success)
    if (!conRest.success) {
      await this.loadingService.dismissLoading();
      throw new Error(PeripheralError.RICOH_CONNECTION_ERROR)
    }
    console.log("RICOH open peripheral gallery select modal page")
    const modal = await this.modalController.create({
      component: PeripheralGallerySelectModalPage,
      componentProps: {
        periphal: peripheral,
        objName: peripheral.label,
        maxImages: maxImages,
      },
      cssClass: 'transparent-modal',
    });
    await this.loadingService.dismissLoading();
    await modal.present();
    const { data } = await modal.onDidDismiss();
    await this.loadingService.showLoading();
    try{
      await this.tryDisconnectPeripheral(peripheral);
    } catch(e) {
      this.logger.warn(
        this.constructor.name,
        'could not disconnect peripheral after gallery flow',
        peripheral.id?.imsId
      );
    }
    // If modal was not canceled simply pass through the data to the caller
    console.log("JB360 Gallery Modal Data", data)
    if (data.canceled) {
      await this.loadingService.dismissLoading();
      return [];
    }

    const selectedFiles: ExtFile[] | ImsImage[] = data.selectedFiles ?? [];
    const filteredImages: ImsImage[] = selectedFiles.filter(
      (it): it is ImsImage => !(it instanceof ExtFile)
    );
    await this.loadingService.dismissLoading();
    return filteredImages;
  }

  public cameraAvailable() {
    return this.availableCameras.length >= 0;
  }

  public get availableCameras(): Peripheral[] {
    return this.peripherals$.getValue().filter((it) => {
      return (
        it.type === PeripheralType.PT_CAMERA_THETA &&
        it.connectionStatus != ConnectionStatus.NOT_CONNECTED
      );
    });
  }

  public async takePicture(peripheral: Peripheral): Promise<ImsImage> {
    console.log(
      this.constructor.name,
      'LBSPeripherals take picture with peripheral',
      peripheral.name
    );
    if (peripheral.connectionStatus != ConnectionStatus.CONNECTED) {
      return;
    }
    let image: ImsImage;
    try {
      switch (peripheral.type) {
        case PeripheralType.PT_CAMERA_THETA:
          const result = await LBSPeripherals.tryTakePicture({
            type: CameraType.RICOH,
            serial: peripheral.softwareId,
          });
          console.log("LBSPeripherals download taken image fromm", result?.pictureUrl)
          image = {
            filePath:
              await this.fileManager.downloadExternalFileToPersistentDataDirectory(
                result.pictureUrl
              ),
            timeStampTaken: Date.now(),
            is360Degree: true,
          };
          console.log("LBSPeripherals downloaded taken image", image.filePath)
          break;
        default:
          this.logger.warn(
            this.constructor.name,
            'Unknown Device',
            peripheral.type
          );
      }
    } catch (e) {
      console.log(
        this.constructor.name,
        'LBSPeripherals error while taking picture with peripheral', e,
        e?.message
      );
      this.errorService.showErrorHandlerResult(
        await this.errorHandlerService.handleError(e)
      );
    }
    return image;
  }

  public async tryStartLivePreview(peripheral: Peripheral): Promise<void> {
    console.log(
      this.constructor.name,
      'LBSPeripherals start live preview with peripheral',
      peripheral.name
    );
    if (peripheral.connectionStatus != ConnectionStatus.CONNECTED) {
      return;
    }
    let result: string | undefined;
    try {
      switch (peripheral.type) {
        case PeripheralType.PT_CAMERA_THETA:
          await LBSPeripherals.tryStartLivePreview({
            type: CameraType.RICOH,
            serial: peripheral.softwareId,
          });

          break;
        default:
          this.logger.warn(
            this.constructor.name,
            'Unknown Device',
            peripheral.type
          );
      }
    } catch (e) {
      console.log(
        this.constructor.name,
        'LBSPeripherals error while taking picture with peripheral',
        e?.message
      );
      this.errorService.showErrorHandlerResult(
        await this.errorHandlerService.handleError(e)
      );
    }
  }

  public async tryStopLivePreview(peripheral: Peripheral): Promise<void> {
    console.log(
      this.constructor.name,
      'LBSPeripherals stop live preview with peripheral',
      peripheral.name
    );
    if (peripheral.connectionStatus != ConnectionStatus.CONNECTED) {
      return;
    }
    try {
      switch (peripheral.type) {
        case PeripheralType.PT_CAMERA_THETA:
          await LBSPeripherals.tryStopLivePreview({
            type: CameraType.RICOH,
            serial: peripheral.softwareId,
          });

          break;
        default:
          this.logger.warn(
            this.constructor.name,
            'Unknown Device',
            peripheral.type
          );
      }
    } catch (e) {
      console.log(
        this.constructor.name,
        'LBSPeripherals error while taking picture with peripheral',
        e?.message
      );
      this.errorService.showErrorHandlerResult(
        await this.errorHandlerService.handleError(e)
      );
    }
  }

  public async tryListPhotos(peripheral: Peripheral): Promise<ImsImage[]> {
    console.log(
      this.constructor.name,
      'LBSPeripherals list photos from peripheral',
      peripheral.name
    );
    if (peripheral.connectionStatus != ConnectionStatus.CONNECTED) {
      return;
    }
    try {
      let photos: ImsImage[] = [];
      switch (peripheral.type) {
        case PeripheralType.PT_CAMERA_THETA:
          const photoResult = await LBSUtils.addTimeoutToPromise(
            LBSPeripherals.tryListPhotos({
            type: CameraType.RICOH,
            serial: peripheral.softwareId,
          }), 10000);

          if (
            photoResult?.photos?.length === 0 ||
            photoResult?.photos == undefined
          ) {
            return [];
          }

          for (const photo of photoResult.photos) {
            var timestampTaken: number | undefined = undefined;
            const parsedTime = parse(
              photo.dateTime,
              'yyyy:MM:dd HH:mm',
              new Date()
            );
            if (parsedTime != undefined && !isNaN(parsedTime.getTime())){
              timestampTaken = parsedTime.getTime();
            } else {
              this.logger.warn(this.constructor.name, 'Invalid dateTime from camera:', photo.dateTime);
            }
            const imsImage: ImsImage = {
              timeStampTaken: timestampTaken,
              webPath: photo.thumbnailUrl,
              filePath: photo.fileUrl,
              is360Degree: true,
            };
            photos.push(imsImage);
          }

          break;
        default:
          this.logger.warn(
            this.constructor.name,
            'Unknown Device',
            peripheral.type
          );
      }
      return photos;
    } catch (e) {
      console.log(
        this.constructor.name,
        'LBSPeripherals error while taking picture with peripheral',
        e?.message
      );
      this.errorService.showErrorHandlerResult(
        await this.errorHandlerService.handleError(e)
      );
    }
  }
  å;

  // Cameras End

  public async trySendScannerCommand(
    peripheral: Peripheral,
    command: ScannerCommand
  ): Promise<boolean> {
    console.log(
      this.constructor.name,
      'LBSPeripherals sent command to peripheral',
      peripheral.name,
      command
    );
    if (peripheral.connectionStatus != ConnectionStatus.CONNECTED) {
      return false;
    }
    let result: { success: boolean };
    try {
      switch (peripheral.type) {
        case PeripheralType.PT_SCANNER_NETUM:
          result = await LBSPeripherals.trySendScannerCommand({
            type: ScannerType.NETUM,
            mac:
              peripheral.connectedMac != null && peripheral.connectedMac != ''
                ? peripheral.connectedMac
                : peripheral.mac,
            command: command,
          });
          break;
        case PeripheralType.PT_PAYMENT_SIX:
        default:
          this.logger.warn(
            this.constructor.name,
            'Unknown Device',
            peripheral.type
          );
          this.errorService.showErrorHandlerResult(
            await this.errorHandlerService.handleError(
              new Error(PeripheralError.UNKNOWN_DEVICE_TYPE)
            )
          );
      }
    } catch (e) {
      console.log(
        this.constructor.name,
        'LBSPeripherals error while connecting to send command',
        e?.message
      );
      this.errorService.showErrorHandlerResult(
        await this.errorHandlerService.handleError(e)
      );
    }
    return result?.success ?? false;
  }

  public async stopScanIfNecessary(peripheral: Peripheral): Promise<void> {
    switch (peripheral.type) {
      case PeripheralType.PT_SCANNER_NETUM:
        await LBSPeripherals.stopScannerDiscoveryIfNecessary({
          type: ScannerType.NETUM,
        });
        break;
      case PeripheralType.PT_PAYMENT_SIX:
      default:
        this.logger.warn(
          this.constructor.name,
          'Unknown Device to stop discovery',
          peripheral.type
        );
    }
  }

  public async tryDisconnectPeripheral(
    peripheral: Peripheral,
    forgetDevice: boolean = false
  ): Promise<void> {
    switch (peripheral.type) {
      case PeripheralType.PT_SCANNER_NETUM:
        await LBSPeripherals.tryDisconnectScanner({
          type: ScannerType.NETUM,
          mac:
            peripheral.connectedMac != null && peripheral.connectedMac != ''
              ? peripheral.connectedMac
              : peripheral.mac,
        });
        break;
      case PeripheralType.PT_PAYMENT_SIX:
        await LBSPeripherals.tryDisconnectTerminal({
          type: TerminalType.SIX,
          id: peripheral.softwareId,
        });
        break;
      case PeripheralType.PT_CAMERA_THETA:
        await LBSPeripherals.tryDisconnectCamera({
          type: CameraType.RICOH,
          serial: peripheral.softwareId,
        });
        break;
      default:
        this.logger.warn(
          this.constructor.name,
          'Unknown Device',
          peripheral.type
        );
    }

    if (forgetDevice) {
      peripheral.connectedMac = '';
      await this.peripheralsDaoService.updateConnectionStatus(peripheral);
      await this.loadLocalPeripherals();
    }
  }

  public async doBalance(peripheral?: Peripheral): Promise<boolean> {
    if (peripheral == undefined) {
      peripheral = this.getConnnectedPeripheralForType(
        PeripheralType.PT_PAYMENT_SIX
      );
      if (peripheral == undefined) {
        this.logger.warn(
          this.constructor.name,
          'No Terminal found to do balance'
        );
        return true;
      }
    }
    try {
      switch (peripheral.type) {
        case PeripheralType.PT_PAYMENT_SIX:
          await LBSPeripherals.doBalance({
            id: peripheral.softwareId,
            type: TerminalType.SIX,
          });
          break;
        case PeripheralType.PT_SCANNER_NETUM:
        default:
          this.logger.warn(
            this.constructor.name,
            'Balance not available for Device with type',
            peripheral.type
          );
      }
      return true;
    } catch (e) {
      await this.errorHandler.handleError(e);
      return false;
    }
  }

  public async doTransaction(
    peripheral: Peripheral,
    paymentRef: string,
    amount: number,
    servicejob?: Servicejob
  ): Promise<TrannsactionResult> {
    try {
      let trx: TrannsactionResult;
      switch (peripheral.type) {
        case PeripheralType.PT_PAYMENT_SIX:
          trx = await LBSPeripherals.doTransaction({
            id: peripheral.softwareId,
            type: TerminalType.SIX,
            amount: amount,
            ref: paymentRef,
          });
          break;
        case PeripheralType.PT_SCANNER_NETUM:
        default:
          this.logger.warn(
            this.constructor.name,
            'Transaction not available for Device with type',
            peripheral.type
          );
      }

      this.logger.log(
        this.constructor.name,
        'payment processed: ',
        trx.cardBrandName,
        trx.customerReceipt,
        trx.merchantReceipt
      );

      if (servicejob) {
        this.logger.log(
          this.constructor.name,
          'found a servicejob start receipt upload immediatly'
        );
        let receiptRAB = new PaymentReceiptRemoteActionBuilder(
          servicejob,
          trx,
          this.appService,
          this.credentialsService,
          this.logger
        );

        let recceiptRA = await receiptRAB.generateRemoteAction([]);
        await this.remoteActionService.sendRemoteAction(recceiptRA);

        this.logger.log(this.constructor.name, 'receipt upload queued');

        servicejob.paidAmtCard = amount;
        servicejob.merchantReceipt = trx.merchantReceipt;
        servicejob.customerReceipt = trx.customerReceipt;
        servicejob.cardType = trx.cardBrandName;
        await this.servicejobsService.updateServicejob(servicejob);
        this.logger.log(
          this.constructor.name,
          'updated payment info on servicejob',
          servicejob.id,
          servicejob.paidAmtCard,
          servicejob.cardType
        );

        if (servicejob.tour != undefined) {
          const tour = this.tourService.getTourById(servicejob.tour.id);
          tour.cardPaymentDone = true;
          await this.tourService.updateTour(tour);
          this.logger.log(
            this.constructor.name,
            'tour cardPaymentDone set to true'
          );
        }
      }

      return trx;
    } catch (e) {
      await this.errorHandler.handleError(e);
      return null;
    }
  }

  // Private Functions

  private async loadLocalPeripherals() {
    try {
      this.logger.log(this.constructor.name, '- Load local Peripherals');
      let peripherals: Peripheral[] =
        await this.peripheralsDaoService.getPeripherals();
      this.logger.log(
        this.constructor.name,
        '- Loaded',
        peripherals.length,
        'local Peripherals'
      );

      this.updatePeripheralsWithStatus(peripherals);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Peripherals: ',
        error?.message
      );
      throw error;
    }
  }

  private updatePeripheralsWithStatus(peripherals: Peripheral[]) {
    for (const peripheral of peripherals) {
      const status = this.peripheralStatusMap.get(peripheral.identifier);
      console.log(
        this.constructor.name,
        'updatePeripheralsWithStatus peripheral',
        peripheral,
        'status',
        status
      );
      if (status?.connection != undefined) {
        console.log(
          this.constructor.name,
          'Update connection status for',
          peripheral.name,
          'to',
          status.connection
        );
        peripheral.connectionStatus = status.connection;
      }
      if (status?.battery != undefined) {
        console.log(
          this.constructor.name,
          'Update battery status for',
          peripheral.name,
          'to',
          status.battery
        );
        peripheral.battery = status.battery;
      }
    }
    console.log(
      this.constructor.name,
      'updatePeripheralsWithStatus',
      peripherals
    );
    this.peripherals$.next(
      peripherals.filter((p) => !p.isPoolDevice || this.assignedPoolPeripheralIds.has(p.id.imsId))
    );
    this.poolPeripherals$.next(
      peripherals.filter((p) => p.isPoolDevice && !this.assignedPoolPeripheralIds.has(p.id.imsId))
    );
    this.logger.log(this.constructor.name, "loaded local peripherals", this.peripherals$.getValue().length)
    this.logger.log(this.constructor.name, "loaded local pool peripherals", this.poolPeripherals$.getValue().length)
  }

  private async connectAvailableDevices() {
    for (let peripheral of this.peripherals$.getValue() ?? []) {
      console.log(
        this.constructor.name,
        'connectAvailableDevices',
        peripheral.name,
        peripheral.connectedMac,
        peripheral.connectedMac == null,
        peripheral.connectedMac === 'null'
      );
      if (
        peripheral.connectedMac != null &&
        peripheral.connectionStatus !== ConnectionStatus.CONNECTED &&
        peripheral.connectionStatus !== ConnectionStatus.CONNECTING
        // &&
        // peripheral.type !== PeripheralType.PT_CAMERA_THETA
      ) {
        console.log(
          this.constructor.name,
          'connectAvailableDevices try connecting to known device'
        );
        try {
          await this.connectPeripheral(peripheral, undefined, true);
        } catch (e) {
          this.logger.warn(
            this.constructor.name,
            'connectAvailableDevices faild on',
            peripheral.name,
            e?.message
          );
        }
      }
    }
  }

  private async loadRemotePeripherals(apiLevel: APILevel, syncKey: string) {
    this.logger.log(
      this.constructor.name,
      '- loadRemotePeripherals with APILevel',
      apiLevel
    );
    if (apiLevel < APILevel.PERIHPERALS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load peripherals init with no peripherals'
      );
      var peripherals: Peripheral[] = [];
      await this.peripheralsDaoService.savePeripherals(peripherals);
      await this.loadLocalPeripherals();
      return;
    }
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new Peripherals from IMS');

      var peripherals: Peripheral[] = [];
      try {
        let peripheralsData: any =
          await this.remoteActionService.sendRemoteAction(
            new RemoteAction({
              endpoint: 'syncperipherals',
              cachable: false,
              fifo: false,
              authenticationRequired: true,
              data: {
                synckey: syncKey,
              },
            })
          );

        this.logger.log(
          this.constructor.name,
          'Downloaded Peripheral Data:',
          peripheralsData
        );

        for (const peripheralData of peripheralsData) {
          peripherals.push(Peripheral.fromDto(peripheralData));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded Peripherals: ',
          peripherals
        );

        await this.peripheralsDaoService.savePeripherals(peripherals);

        const removedPeripherals = this.tempPeripherals.filter(
          (x) => peripherals.findIndex((it) => it.id.imsId === x.id.imsId) < 0
        );
        await this.peripheralsDaoService.deletePeripherals(removedPeripherals);

        console.log(
          this.constructor.name,
          'Found ',
          removedPeripherals.length,
          'removed Peripherals - stop running connection threads and clear status'
        );
        for (const peripheral of removedPeripherals) {
          if (peripheral.connectedMac != null) {
            try {
              await this.tryDisconnectPeripheral(peripheral);
              this.peripheralStatusMap.delete(peripheral.connectedMac);
            } catch (e) {
              this.logger.warn(
                this.constructor.name,
                'could not reset peripheral after deletion',
                peripheral?.id?.imsId
              );
            }
          }
        }

        await this.loadLocalPeripherals();
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Peripherals: ',
          error?.message
        );
        throw error;
      }
    }
  }
}
